import { useState } from 'react'
import { PiX } from 'react-icons/pi'
import { Alert } from 'components/FbUI/Alert'
import { cn } from 'components/UI/cn'

export const ZIP_REGEX = /^\d{5}$/
export const MAX_ZIPS = 10

export interface ZipChipInputProps {
  zips: string[]
  onChange: (zips: string[]) => void
}

export function parseZipEntry(raw: string): {
  valid: string[]
  invalid: string[]
} {
  const tokens = raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
  const valid: string[] = []
  const invalid: string[] = []
  for (const token of tokens) {
    if (ZIP_REGEX.test(token)) {
      valid.push(token)
    } else {
      invalid.push(token)
    }
  }
  return { valid, invalid }
}

export function ZipChipInput({ zips, onChange }: ZipChipInputProps) {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  const commit = (raw: string) => {
    const { valid, invalid } = parseZipEntry(raw)
    if (invalid.length > 0) {
      setError(`Not a 5-digit ZIP: ${invalid.join(', ')}`)
    } else {
      setError(null)
    }
    if (valid.length === 0) {
      return
    }
    const merged = Array.from(new Set([...zips, ...valid]))
    if (merged.length > MAX_ZIPS) {
      setError(`Up to ${MAX_ZIPS} ZIP codes.`)
      onChange(merged.slice(0, MAX_ZIPS))
    } else {
      onChange(merged)
    }
    setText('')
  }

  const remaining = MAX_ZIPS - zips.length

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {zips.map((zip) => (
          <span
            key={zip}
            className="inline-flex items-center gap-1 rounded bg-surface-hover px-2 py-0.5 text-xs text-fg"
          >
            {zip}
            <button
              type="button"
              aria-label={`Remove ${zip}`}
              className="text-fg-tertiary hover:text-fg-secondary"
              onClick={() => onChange(zips.filter((z) => z !== zip))}
            >
              <PiX size={12} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        aria-label="ZIP code entry"
        value={text}
        placeholder={remaining > 0 ? 'Add ZIP code…' : 'Max 10 ZIPs'}
        disabled={remaining <= 0}
        className={cn(
          'w-full rounded border border-line px-2 py-1 text-sm',
          'focus:border-brand focus:outline-none'
        )}
        onChange={(e) => {
          setText(e.target.value)
          setError(null)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault()
            commit(text)
          } else if (e.key === 'Backspace' && text === '' && zips.length > 0) {
            setError(null)
            onChange(zips.slice(0, -1))
          }
        }}
        onPaste={(e) => {
          e.preventDefault()
          commit(e.clipboardData.getData('text'))
        }}
        onBlur={() => {
          if (text.trim()) commit(text)
        }}
      />
      <div className="mt-1 text-xxs text-fg-tertiary">
        {Math.max(0, remaining)} left
      </div>
      {error && <Alert color="danger" description={error} className="mt-2" />}
    </div>
  )
}
